<template>
  <div class="modal-overflow" style="padding-top: 10px;">
    <div class="project-details">
      <div class="row">
        <div class="col-md-8">
          <div class="row" @mouseover="closeDepartments">
            <div class="col-md-7">
              <div class="form-group">
                <label>Name</label>
                <button @click="generateProjectName" class="btn btn-fill btn-info btn-sm btn-save tiny-label-button"> + </button>
                <el-input type="text"
                       name="name"
                       v-validate="modelValidations.name"
                       v-model="localProject.name"
                       maxlength="256"
                       placeholder="Name"
                       clearable
                       @blur="validateNameIsUnique" />
                <small class="text-danger" style="display: block" v-show="errors.has('name')">
                  {{ getError('name') }}
                </small>
              </div>
            </div>
            <div class="col-md-5">
              <div class="form-group">
                <label>Type</label>
                <el-select class="select"
                           name="projectType"
                           clearable
                           size="large"
                           placeholder="Type"
                           v-validate="modelValidations.projectType"
                           data-vv-as="type"
                           v-model="localProject.projectType">
                  <el-option v-for="option in projectTypes"
                             class="select"
                             :value="option.value"
                             :label="option.label"
                             :key="option.label">
                  </el-option>
                </el-select>
                <small class="text-danger" v-show="errors.has('projectType')">
                  {{ getError('projectType') }}
                </small>
              </div>
            </div>
            <div class="col-md-12" style="margin-top: 15px;">
              <div class="box-visual">
                <label>Notes</label>
                <div class="pull-right" style="margin-top: -15px;">
                  <button type="button" v-if="localProject.departments.length > 0" @click="editNote" class="btn btn-fill btn-lg btn-info el-icon-edit" style="padding: 8px;"></button>
                </div>
                <tabs value="Description" ref="notesTabs">
                  <tab-pane v-for="department in matchingDepartments" :title="`${department.name}${checkForNotes(department.id)}`" :key="department.id">
                    <div v-html="getNotesByDepartmentId(department.id)" @dblclick="editNote"></div>
                  </tab-pane>
                </tabs>
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-4">
          <div class="form-group">
            <label>Departments</label>
            <el-select multiple class="select"
                       name="departments"
                       size="large"
                       clearable
                       @focus="setActiveTab()"
                       ref="departmentsSelect"
                       placeholder="Departments"
                       v-validate="modelValidations.departments"
                       v-model="localProject.departments">
              <el-option v-for="option in sortedDepartments"
                         class="select"
                         :value="option.value"
                         :label="option.label"
                         :key="option.label">
              </el-option>
            </el-select>
            <small class="text-danger" v-show="errors.has('departments')">
              {{ getError('departments') }}
            </small>
          </div>
        </div>
      </div>
    </div>
    <div class="project-address">
      <h4>Address</h4>
      <div class="row">
        <div class="col-lg-2">
          <div class="form-group">
            <el-checkbox class="remember-address-field" v-model="remember.city" @change="rememberCityChanged">City</el-checkbox>
            <el-autocomplete
              style="width:100%"
              name="city"
              ref="city"
              class="inline-input"
              clearable
              v-model="localCity"
              :fetch-suggestions="findCities"
              :trigger-on-focus="false"
              value-key="name"
              placeholder="City"
              @blur="verifyCityAutocompleteSelected"
              @select="setCityLoadCountiesZips">
            </el-autocomplete>
            <small class="text-danger" v-show="errors.has('city')">
              {{ getError('city') }}
            </small>
            <small class="text-danger" v-show="showInvalidAutocompleteWarning('city')">
              {{ fieldNotValidError('City') }}
            </small>
          </div>
        </div>
        <div class="col-lg-2">
          <div class="form-group">
            <el-checkbox class="remember-address-field" v-model="remember.streetNumber">Street Number</el-checkbox>
            <el-input type="text"
                      name="streetNumber"
                      placeholder="Street Number"
                      clearable
                      v-validate="modelValidations.streetNumber"
                      v-model="localProject.address.streetNumber" />
            <small class="text-danger" v-show="errors.has('streetNumber')">
              {{ getError('streetNumber') }}
            </small>
          </div>
        </div>
        <div class="col-lg-2">
          <div class="form-group">
            <el-checkbox class="remember-address-field" v-model="remember.streetName" @change="maybeAlsoRememberCity">Street Name</el-checkbox>
            <el-autocomplete
              style="width:100%"
              ref="streetName"
              name="streetName"
              class="inline-input"
              clearable
              v-model="localStreetName"
              v-validate="modelValidations.streetName"
              :fetch-suggestions="findStreets"
              :trigger-on-focus="false"
              value-key="name"
              placeholder="Street Name"
              @focus="verifyCitySelected"
              @blur="verifyStreetNameAutocompleteSelected"
              @select="setStreet">
            </el-autocomplete>
            <small class="text-danger" v-show="errors.has('streetName')">
              {{ getError('streetName') }}
            </small>
            <small class="text-danger" v-show="showInvalidAutocompleteWarning('streetName')">
              {{ fieldNotValidError('Street Name') }}
            </small>
          </div>
        </div>
        <div class="col-lg-2">
          <div class="form-group">
            <el-checkbox class="remember-address-field" v-model="remember.county" @change="maybeAlsoRememberCity">County</el-checkbox>
            <el-select class="select"
                       name="county"
                       size="large"
                       clearable
                       placeholder="County"
                       @focus="verifyCitySelected"
                       v-validate="modelValidations.county"
                       v-model="localProject.address.county">
              <el-option v-for="option in counties"
                         class="select"
                         :value="option.value"
                         :label="option.label"
                         :key="option.label">
              </el-option>
            </el-select>
            <small class="text-danger" v-show="errors.has('county')">
              {{ getError('county') }}
            </small>
          </div>
        </div>
        <div class="col-lg-2">
          <div class="form-group">
            <el-checkbox class="remember-address-field" v-model="remember.state">State</el-checkbox>
            <el-select class="select"
                       size="large"
                       name="state"
                       clearable
                       v-validate="modelValidations.state"
                       v-model="localProject.address.state">
              <el-option v-for="option in states"
                         class="select"
                         :value="option.value"
                         :label="option.label"
                         :key="option.label">
              </el-option>
            </el-select>
            <small class="text-danger" v-show="errors.has('state')">
              {{ getError('state') }}
            </small>
          </div>
        </div>
      </div><!-- row -->
      <div class="row">
        <div class="col-lg-2">
          <div class="form-group">
            <el-checkbox class="remember-address-field" v-model="remember.buildingNumber">Building Number</el-checkbox>
            <el-input type="text"
                      name="buildingNumber"
                      placeholder="Building Number"
                      clearable
                      v-validate="modelValidations.buildingNumber"
                      v-model="localProject.address.buildingNumber" />
            <small class="text-danger" v-show="errors.has('buildingNumber')">
              {{ getError('buildingNumber') }}
            </small>
          </div>
        </div>
        <div class="col-lg-2">
          <div class="form-group">
            <el-checkbox class="remember-address-field" v-model="remember.floorNumber">Floor Number</el-checkbox>
            <el-input type="text"
                      name="floorNumber"
                      placeholder="Floor Number"
                      clearable
                      v-validate="modelValidations.floorNumber"
                      v-model="localProject.address.floorNumber" />
            <small class="text-danger" v-show="errors.has('floorNumber')">
              {{ getError('floorNumber') }}
            </small>
          </div>
        </div>
        <div class="col-lg-2">
          <div class="form-group">
            <el-checkbox class="remember-address-field" v-model="remember.suiteNumber">Suite Number</el-checkbox>
            <el-input type="text"
                      name="suiteNumber"
                      placeholder="Suite Number"
                      clearable
                      v-validate="modelValidations.suiteNumber"
                      v-model="localProject.address.suiteNumber" />
            <small class="text-danger" v-show="errors.has('suiteNumber')">
              {{ getError('suiteNumber') }}
            </small>
          </div>
        </div>
        <div class="col-lg-2">
          <div class="form-group">
            <el-checkbox class="remember-address-field" v-model="remember.zip" @change="maybeAlsoRememberCity">Zip</el-checkbox>
            <el-select class="select"
                       name="zip"
                       size="large"
                       clearable
                       placeholder="Zip"
                       @focus="verifyCitySelected"
                       v-validate="modelValidations.zip"
                       v-model="localProject.address.zip">
              <el-option v-for="option in zipCodes"
                         class="select"
                         :value="option.value"
                         :label="option.label"
                         :key="option.label">
              </el-option>
            </el-select>
            <small class="text-danger" v-show="errors.has('zip')">
              {{ getError('zip') }}
            </small>
          </div>
        </div>
        <div class="col-lg-2">
          <div class="form-group">
            <el-checkbox class="remember-address-field" v-model="remember.jurisdiction">Jurisdiction</el-checkbox>
            <el-select class="select"
                       name="jurisdiction"
                       size="large"
                       clearable
                       placeholder="Jurisdiction"
                       v-validate="modelValidations.jurisdiction"
                       v-model="localProject.address.jurisdiction">
              <el-option v-for="option in constants.JURISDICTIONS"
                         class="select"
                         :value="option.value"
                         :label="option.label"
                         :key="option.label">
              </el-option>
            </el-select>
            <small class="text-danger" v-show="errors.has('jurisdiction')">
              {{ getError('jurisdiction') }}
            </small>
          </div>
        </div>
      </div><!-- row -->
      <div class="row">
        <div class="col-lg-2">
          <div class="form-group">
            <el-checkbox class="remember-address-field" v-model="remember.lotNumber">Lot Number</el-checkbox>
            <el-input type="text"
                      name="lotNumber"
                      placeholder="Lot Number"
                      clearable
                      v-validate="modelValidations.lotNumber"
                      v-model="localProject.address.lotNumber" />
            <small class="text-danger" v-show="errors.has('lotNumber')">
              {{ getError('lotNumber') }}
            </small>
          </div>
        </div>
        <div class="col-lg-2">
          <div class="form-group">
            <el-checkbox class="remember-address-field" v-model="remember.blockNumber">Block Number</el-checkbox>
            <el-input type="text"
                      name="blockNumber"
                      placeholder="Block Number"
                      clearable
                      v-validate="modelValidations.blockNumber"
                      v-model="localProject.address.blockNumber" />
            <small class="text-danger" v-show="errors.has('blockNumber')">
              {{ getError('blockNumber') }}
            </small>
          </div>
        </div>
        <div class="col-lg-2">
          <div class="form-group">
            <el-checkbox class="remember-address-field" v-model="remember.phase">Phase</el-checkbox>
            <el-input type="text"
                      name="phase"
                      placeholder="Phase"
                      clearable
                      v-validate="modelValidations.phase"
                      v-model="localProject.address.phase" />
            <small class="text-danger" v-show="errors.has('phase')">
              {{ getError('phase') }}
            </small>
          </div>
        </div>
        <div class="col-lg-2">
          <div class="form-group">
            <el-checkbox class="remember-address-field" v-model="remember.section">Section</el-checkbox>
            <el-input type="text"
                      name="section"
                      placeholder="Section"
                      clearable
                      v-validate="modelValidations.section"
                      v-model="localProject.address.section" />
            <small class="text-danger" v-show="errors.has('section')">
              {{ getError('section') }}
            </small>
          </div>
        </div>
        <div class="col-md-2">
          <div class="form-group">
            <el-checkbox class="remember-address-field" v-model="remember.legalDescription">Legal Description</el-checkbox>
            <el-input type="text"
                      name="legalDescription"
                      placeholder="Legal Description"
                      clearable
                      v-validate="modelValidations.legalDescription"
                      v-model="localProject.address.legalDescription" />
            <small class="text-danger" v-show="errors.has('legalDescription')">
              {{ getError('legalDescription') }}
            </small>
          </div>
        </div>
      </div>
      <div class="col-lg-2 col-half-offset">
        <div class="form-group">
          <el-checkbox class="remember-address-field" v-model="remember.legalSubdivisionId">Legal Subdivision</el-checkbox>
          <el-autocomplete
            style="width:100%"
            ref="legalSubdivisionId"
            name="legalSubdivision"
            class="inline-input"
            clearable
            data-vv-name="legal subdivision"
            v-model="localLegalSubdivisionName"
            v-validate="modelValidations.legalSubdivisionId"
            :fetch-suggestions="findLegalSubdivisions"
            :trigger-on-focus="false"
            value-key="name"
            placeholder="Legal Subdivision"
            @blur="verifyLegalSubdivisionAutocompleteSelected"
            @select="setLegalSubdivision">
          </el-autocomplete>
          <small class="text-danger" v-show="errors.has('legalSubdivisionId')">
            {{ getError('legalSubdivisionId') }}
          </small>
          <small class="text-danger" v-show="showInvalidAutocompleteWarning('legalSubdivisionId')">
            {{ fieldNotValidError('Legal Subdivision') }}
          </small>
        </div>
      </div>
    </div><!-- row -->
    <div class="row" style="margin-top: 15px" v-if="showButtons">
      <div class="col-md-12">
        <button type="button" @click="resetState" class="btn btn-fill btn-info btn-wd">Reset</button>
        <button type="submit" @click.prevent="validateAndSubmit" class="btn btn-fill btn-info btn-wd pull-right" :disabled="isProcessing">Save</button>
      </div>
    </div>
  </div>
</template>

<script>
import {mapGetters} from 'vuex'
import {mapFields} from 'vee-validate'
import {
  notifySuccess,
  notifyError,
  kvpsForSelectFromArray,
  verifyAutocompleteSelected,
  objectByName,
  objectBy,
  noteIconSlice,
  findCities,
  setCityLoadCountiesZips,
  findStreets,
  findLegalSubdivisions,
  autocompleteTextButNoValue,
  autocompleteTextButNoValueMessage,
  maybeGenerateProjectName
} from 'src/utilities/utils'
import Constants from 'src/utilities/constants'
import CKEditor from '@ckeditor/ckeditor5-vue'
import ckEditorConfig from 'src/utilities/ckeditor'
import {Tabs, TabPane} from 'src/components/UIComponents'
import {OK, CREATED} from 'http-status-codes'
import _ from 'lodash'

export default {
  props: ['data'],
  components: {
    ckeditor: CKEditor.component,
    TabPane,
    Tabs
  },
  computed: {
    ...mapFields([
      'name',
      'projectType',
      'description',
      'departments',
      'city',
      'subdivision',
      'county',
      'state',
      'zip',
      'jurisdiction',
      'streetNumber',
      'suiteNumber',
      'buildingNumber',
      'streetName',
      'lotNumber',
      'blockNumber',
      'legalDescription',
      'legalSubdivisionId',
      'phase',
      'section'
    ]),
    ...mapGetters({
      sortedDepartments: 'departments/kvpsForSelect',
      projectTypes: 'types/project',
      states: 'states',
      cities: 'address/cities',
      allDepartments: 'departments/results',
      user: 'security/user',
      activeModals: 'modalTracker/activeModals'
    }),
    isProcessing() {
      return this.$store.getters['processing']
    },
    isNotActiveTabInWizard() {
      return this.createPath === 'createWizard' && !this.$parent.$parent.tabs[1].active
    },
    showButtons() {
      return this.data && this.data.showButtons
    },
    suppressCreateProjectNotification() {
      return this.data && this.data.suppressCreateProjectNotification
    },
    createPath() {
      if (this.data.source === Constants.CREATE_PROJECT.FROM_PROJECT_SEARCH_WIZARD) {
        return 'createWizard'
      }
      return 'create'
    },
    matchingDepartments() {
      const selectedDepartments = this.$store.get(`projects/${this.createPath}/edit@departments`)

      // Hacky solution for issue with multiple active tabs.
      if (this.activeTab) {
        for (const tab of this.$refs.notesTabs.tabs) {
          (tab._props.title === this.activeTab || tab._props.title === this.activeTab + ' ★') ? tab.active = true : tab.active = false
        }
      }

      return _.filter(this.allDepartments, (department) => selectedDepartments.includes(department.id))
    }
  },
  data () {
    return {
      isMounted: false, // Prevents watchers from clearing store on mount
      constants: Constants,
      ckEditorConfig,
      localCity: null,
      localStreetName: null,
      localLegalSubdivisionName: null,
      counties: [],
      zipCodes: [],
      activeTab: null,
      localProject: {
        name: null,
        projectType: null,
        notesByDepartment: {},
        departments: [],
        address: {
          streetNumber: null,
          county: null,
          state: Constants.ATS.DEFAULT.STATE,
          buildingNumber: null,
          floorNumber: null,
          suiteNumber: null,
          zip: null,
          jurisdiction: null,
          lotNumber: null,
          blockNumber: null,
          phase: null,
          section: null,
          legalDescription: null,
        }
      },
      remember: {
        city: false,
        streetNumber: false,
        streetName: false,
        county: false,
        state: false,
        buildingNumber: false,
        floorNumber: false,
        suiteNumber: false,
        zip: false,
        jurisdiction: false,
        lotNumber: false,
        blockNumber: false,
        phase: false,
        section: false,
        legalDescription: false,
        legalSubdivisionId: false
      },
      modelValidations: {
        name: {
          required: true,
          max: 128
        },
        projectType: {
          required: true // BUG: this doesn't work with el-select, set default value in mounted() as a workaround
        },
        description: {
          max: 4096
        },
        departments: {
          required: true
        },
        city: {},
        county: {},
        subdivision: {},
        state: {},
        zip: {},
        jurisdiction: {},
        streetNumber: {
          max: 16
        },
        suiteNumber: {},
        buildingNumber: {},
        floorNumber: {},
        streetName: {},
        lotNumber: {},
        blockNumber: {},
        legalSubdivisionId: {},
        phase: {},
        section: {}
      }
    }
  },
  methods: {
    showInvalidAutocompleteWarning(refAndPathName) {
      // $ref has to match prop in store
      const path = `projects/${this.createPath}/edit@address.`
      return autocompleteTextButNoValue(this, refAndPathName, this.$store.get(path + refAndPathName))
    },
    fieldNotValidError(field) {
      return autocompleteTextButNoValueMessage(field)
    },
    async validateNameIsUnique() {
      const response = await this.$store.dispatch('projects/fetchExistsByName', this.$store.get(`projects/${this.createPath}/edit@name`))
      if (OK === response.status) {
        if (response.data) {
          notifyError(this, 'Project names must be unique. Please enter a different project name.')
          return
        }
        this.localProject.name = this.$store.get(`projects/${this.createPath}/edit@name`)
        return
      }
      notifyError(this, 'Failed to validate name.')
    },
    setActiveTab() {
      const activeTab = objectBy(this.$refs.notesTabs.tabs, 'active', true)
      if (activeTab) {
        this.activeTab = activeTab._props.title
      }
    },
    closeDepartments() {
      // EXPERIMENTAL: close departments multi-select when mouse cursor leaves the input
      if (this.$refs.departmentsSelect) {
        this.$refs.departmentsSelect.blur()
      }
    },
    editNote() {
      this.setActiveTab()
      const department = this.getDepartmentByActiveTab()
      if (department) {
        const notesByDepartment = this.$store.get(`projects/${this.createPath}/edit@notesByDepartment`)
        const payload = {
          department: department,
          notes: notesByDepartment[department.id] || '',
          commitTo: `projects/${this.createPath}/updateProjectNotes`
        }
        this.$eventHub.$emit('show-modal-3', 'Project Notes', payload, '900px')
      }
    },
    getNotesByDepartmentId(departmentId) {
      const notesByDepartment = this.$store.get(`projects/${this.createPath}/edit@notesByDepartment`)
      return notesByDepartment[departmentId] || ''
    },
    checkForNotes(departmentId) {
      if (this.$store.get(`projects/${this.createPath}/edit@notesByDepartment`)[departmentId]) {
        return ' ★'
      }
      return ''
    },
    getDepartmentByActiveTab() {
      const activeTab = objectBy(this.$refs.notesTabs.tabs, 'active', true)
      let activeTabDepartment = activeTab._props.title
      if (!activeTab) {
        console.error('Unable to load an active Tab')
        return false
      }
      if (activeTabDepartment.includes('★')) {
        activeTabDepartment = noteIconSlice(activeTabDepartment)
      }
      return objectByName(this.allDepartments, activeTabDepartment)
    },
    verifyCitySelected() {
      if (!this.localCity || !this.$store.get(`projects/${this.createPath}/edit@address.city`)) {
        this.$eventHub.$emit('show-modal-6', 'No City Error', null, '500px')
      }
    },
    async findLegalSubdivisions(text, cb) {
      const path = `projects/${this.createPath}/edit@address.`
      findLegalSubdivisions(this, text, cb, path)
    },
    setLegalSubdivision(legalSubdivision) {
      this.localLegalSubdivisionName = legalSubdivision.name
      this.$store.set(`projects/${this.createPath}/edit@address.legalSubdivisionId`, legalSubdivision.id)
    },
    clearLegalSubdivision() {
      this.localLegalSubdivisionName = null
    },
    verifyLegalSubdivisionAutocompleteSelected() {
      verifyAutocompleteSelected(this, `projects/${this.createPath}/edit@address.legalSubdivisionId`, 'legal subdivision', this.localLegalSubdivisionName)
    },
    async findCities(text, cb) {
      const path = `projects/${this.createPath}/edit@address.city`
      findCities(this, text, cb, path)
    },
    setCityLoadCountiesZips(city) {
      if (this.isNotActiveTabInWizard) {
        return
      }

      const path = `projects/${this.createPath}/edit@address.city`
      setCityLoadCountiesZips(this, city, path)
    },
    verifyCityAutocompleteSelected() {
      if (this.localCity !== this.$store.get(`projects/${this.createPath}/edit@address.city`)) {
        verifyAutocompleteSelected(this, `projects/${this.createPath}/edit@address.city`, 'city', this.localCity)
        this.resetDependentAutocompleteFields()
      }
    },
    async findStreets(text, cb) {
      const path = `projects/${this.createPath}/edit@address.`
      findStreets(this, text, cb, path)
    },
    setStreet(street) {
     if (this.isNotActiveTabInWizard) {
       return
     }

      this.localStreetName = street.name
      this.$store.set(`projects/${this.createPath}/edit@address.streetName`, street.name)
    },
    clearStreet() {
      this.localStreetName = null
    },
    verifyStreetNameAutocompleteSelected() {
      verifyAutocompleteSelected(this, `projects/${this.createPath}/edit@address.streetName`, 'street name', this.localStreetName)
    },
    generateProjectName() {
      maybeGenerateProjectName(this, `projects/${this.createPath}/edit`)
    },
    resetState() {
      this.localProject.name = null
      this.localProject.notesByDepartment = {}
      this.localProject.projectType = null
      this.localProject.departments = []
      for (const field in this.localProject.address) {
        if (!this.remember[field]) {
          this.localProject.address[field] = null
        }
      }
      if (!this.remember.city) {
        this.localCity = null
      }
      if (!this.remember.streetName) {
        this.localStreetName = null
      }
      if (!this.remember.legalSubdivisionId) {
        this.localLegalSubdivisionName = null
      }
      if (!this.remember.state) {
        this.localProject.address.state = Constants.ATS.DEFAULT.STATE
      }
      this.maybeSetDefaultDepartment()
      this.$store.set(`projects/${this.createPath}/reset`)
    },
    getError(fieldName) {
      return this.errors.first(fieldName)
    },
    async validateAndSubmit() {
      if (await this.$validator.validateAll()) {
        const response = await this.$store.dispatch('save2', {
          endpoint: 'projects',
          data: this.$store.get(`projects/${this.createPath}/edit`)
        })

        if (CREATED === response.status) {
          this.$eventHub.$emit('projectCreated', response.data)
          if (!this.suppressCreateProjectNotification) {
            notifySuccess(this, 'Project Created Successfully')
          }
          this.$emit('close')
          return true
        } else if (response.errors){
          let msg = 'Error Creating Project: '
          if (response.msg) {
            msg += response.msg
          }
          notifyError(this, msg, response.errors)
          return false
        }
      }
    },
    clearCity() {
      this.localCity = null
    },
    resetDependentAutocompleteFields() {
      this.counties = []
      this.zipCodes = []
      this.$store.set('address/cities', [])
      this.localStreetName = null
      this.localProject.address.zip = null
      this.localProject.address.county = null
      this.$store.set(`projects/${this.createPath}/edit@address.city`, null)
    },
    async copySearchToCreate() {
      const rememberFields = this.$store.get(`projects/${this.createPath}/remember`)

      let projectSearchQuery = this.$store.get('projects/search/query')
      if (this.data.source === Constants.CREATE_PROJECT.FROM_PROJECT_SEARCH_WIZARD) {
        projectSearchQuery = this.$store.get('projects/searchWizard/query')
      }

      if (projectSearchQuery[Constants.PROJECT_FIELDS.SEARCH] != null) {
        this.localProject.name = projectSearchQuery[Constants.PROJECT_FIELDS.SEARCH]
      }

      if (projectSearchQuery[Constants.PROJECT_FIELDS.PROJECT_TYPE] != null) {
        this.localProject.projectType = projectSearchQuery[Constants.PROJECT_FIELDS.PROJECT_TYPE]
      }

      if (projectSearchQuery[Constants.PROJECT_FIELDS.LEGAL_SUBDIVISION] != null && !rememberFields[Constants.PROJECT_FIELDS.LEGAL_SUBDIVISION_ID]) {
        await this.fetchLegalSubdivision(projectSearchQuery[Constants.PROJECT_FIELDS.LEGAL_SUBDIVISION])
      }

      for (const field in projectSearchQuery) {
        if (projectSearchQuery[field] != null  && !rememberFields[field] && field !== Constants.PROJECT_FIELDS.COUNTY
            && field !== Constants.PROJECT_FIELDS.ZIP && _.has(this.localProject.address, field)) {
          this.localProject.address[field] = projectSearchQuery[field]
        }
      }

      if (projectSearchQuery[Constants.PROJECT_FIELDS.CITY] != null && !rememberFields[Constants.PROJECT_FIELDS.CITY]) {
        await this.$store.dispatch('address/cityStartsWith', projectSearchQuery[Constants.PROJECT_FIELDS.CITY])
        const cities = this.$store.get('address/cities')

        if (cities.length === 1) {
          this.setCityLoadCountiesZips(cities[0])

          if (projectSearchQuery[Constants.PROJECT_FIELDS.STREET_NAME] != null && !rememberFields[Constants.PROJECT_FIELDS.STREET_NAME]) {
            const payload = {
              city: this.localCity,
              streetName: projectSearchQuery[Constants.PROJECT_FIELDS.STREET_NAME]
            }
            await this.$store.dispatch('address/streetEqualsAndCityEquals', payload)
            const streets = this.$store.get('address/streets')

            if (streets && streets.length === 1) {
              this.setStreet(streets[0])
            } else {
              notifyError(this, 'Unable to match Street Name to City')
            }
          }

          if (projectSearchQuery[Constants.PROJECT_FIELDS.COUNTY] != null && !rememberFields[Constants.PROJECT_FIELDS.COUNTY]) {
            if (this.counties.find(({value}) => value === projectSearchQuery[Constants.PROJECT_FIELDS.COUNTY])) {
              this.localProject.address.county = projectSearchQuery[Constants.PROJECT_FIELDS.COUNTY]
            } else {
              notifyError(this, 'Unable to match County to City')
            }
          }

          if (projectSearchQuery[Constants.PROJECT_FIELDS.ZIP] != null && !rememberFields[Constants.PROJECT_FIELDS.ZIP]) {
            if (this.zipCodes.find(({value}) => value === projectSearchQuery[Constants.PROJECT_FIELDS.ZIP])) {
              this.localProject.address.zip = projectSearchQuery[Constants.PROJECT_FIELDS.ZIP]
            } else {
              notifyError(this, 'Unable to match Zip Code to City')
            }
          }
        } else {
          notifyError(this, 'Unable to find City')
        }
      }

      if (projectSearchQuery[Constants.PROJECT_FIELDS.CITY] == null && (projectSearchQuery[Constants.PROJECT_FIELDS.ZIP] != null
          || projectSearchQuery[Constants.PROJECT_FIELDS.COUNTY] != null || projectSearchQuery[Constants.PROJECT_FIELDS.STREET_NAME] != null)) {
        notifyError(this, 'Unable to match Street Name, County, or Zip without City')
      }

      if (rememberFields[Constants.PROJECT_FIELDS.CITY] && (projectSearchQuery[Constants.PROJECT_FIELDS.ZIP] != null
          || projectSearchQuery[Constants.PROJECT_FIELDS.COUNTY] != null || projectSearchQuery[Constants.PROJECT_FIELDS.STREET_NAME] != null)) {
        notifyError(this, 'City is being remembered. Cannot match Street Name, County, or Zip from Search')
      }
    },
    maybeAlsoRememberCity(value) {
      if (value) {
        this.remember.city = true
      }
    },
    rememberCityChanged(value) {
      if (!value) {
        this.remember.county = false
        this.remember.zip = false
        this.remember.streetName = false
      }
    },
    maybeSetDefaultDepartment() {
      if (_.get(this.user, 'preferences.global.defaultDepartment')) {
        this.localProject.departments.push(this.user.preferences.global.defaultDepartment)
        this.$store.set(`projects/${this.createPath}/addDepartment`, this.user.preferences.global.defaultDepartment)
      }
    },
    refreshNotes() {
      this.$forceUpdate()
    },
    async fetchLegalSubdivision(lsid) {
      const response = await this.$store.dispatch('fetchById', { endpoint: 'legal-subdivisions', id: lsid })
      if (OK === response.status) {
        this.setLegalSubdivision(response.data)
      }
    },
    async setRememberedFields() {
      const rememberFields = this.$store.get(`projects/${this.createPath}/remember`)
      for (const field in rememberFields) {
        this.remember[field] = rememberFields[field]
      }

      const fieldValues = this.$store.get(`projects/${this.createPath}/edit@address`)
      for (const field in this.remember) {
        if (this.remember[field]) {
          if (field === Constants.PROJECT_FIELDS.CITY && fieldValues[field] != null) {
            this.localCity = fieldValues[Constants.PROJECT_FIELDS.CITY]
            const response = await this.$store.dispatch('cities/fetchByName', this.localCity)
            if (OK === response.status) {
              this.counties = kvpsForSelectFromArray(response.data.counties)
              this.zipCodes = kvpsForSelectFromArray(response.data.zipCodes)
            }
          }

          if (field === Constants.PROJECT_FIELDS.STREET_NAME && fieldValues[field] != null) {
            this.localStreetName = fieldValues[Constants.PROJECT_FIELDS.STREET_NAME]
          }

          if (field === Constants.PROJECT_FIELDS.LEGAL_SUBDIVISION_ID && fieldValues[field] != null) {
            this.fetchLegalSubdivision(fieldValues[field])
          }

          if (field !== Constants.PROJECT_FIELDS.CITY && field !== Constants.PROJECT_FIELDS.STREET_NAME
              && field !== Constants.PROJECT_FIELDS.LEGAL_SUBDIVISION_ID && fieldValues[field] != null) {
            this.localProject.address[field] = fieldValues[field]
          }
        }
      }
    }
  },
  beforeCreate() {
    this.$store.cache.dispatch('types/fetchByEntityType', 'Project')
  },
  beforeMount() {
    this.maybeSetDefaultDepartment()
  },
  async mounted() {
    this.$store.set('address/cities', [])
    this.$store.set('address/streets', [])

    this.$eventHub.$on('citySaved', this.setCityLoadCountiesZips)
    this.$eventHub.$on('noCitySaved', this.clearCity)
    this.$eventHub.$on('streetSaved', this.setStreet)
    this.$eventHub.$on('noStreetSaved', this.clearStreet)
    this.$eventHub.$on('copyQueryToCreate', this.copySearchToCreate)
    this.$eventHub.$on('legalSubdivisionSaved', this.setLegalSubdivision)
    this.$eventHub.$on('noLegalSubdivisionSaved', this.clearLegalSubdivision)
    this.$eventHub.$on('projectNotesUpdated', this.refreshNotes)

    await this.setRememberedFields()
    this.isMounted = true
  },
  destroyed() {
    this.$eventHub.$off('citySaved')
    this.$eventHub.$off('noCitySaved')
    this.$eventHub.$off('streetSaved')
    this.$eventHub.$off('noStreetSaved')
    this.$eventHub.$off('legalSubdivisionSaved')
    this.$eventHub.$off('noLegalSubdivisionSaved')
    this.$eventHub.$off('copyQueryToCreate')
    this.$eventHub.$off('projectNotesUpdated')
    this.resetState()
    this.isMounted = false
  },
  watch: {
    localCity: {
      handler: function(city) {
        if (!city) {
          this.resetDependentAutocompleteFields()
        }
      }
    },
    localStreetName: {
      handler: function(streetName) {
        if (!streetName) {
          this.$store.set(`projects/${this.createPath}/edit@address.streetName`, null)
        }
      }
    },
    localLegalSubdivisionName: {
      handler: function(localLegalSubdivisionName) {
        if (!localLegalSubdivisionName) {
          this.$store.set(`projects/${this.createPath}/edit@address.legalSubdivisionId`, null)
        }
      }
    },
    remember: {
      handler: function(rememberFields) {
        if (this.isMounted) {
          for (const field in rememberFields) {
            this.$store.set(`projects/${this.createPath}/remember@${field}`, rememberFields[field])
          }
        }
      },
      deep: true
    },
    localProject: {
      handler: function(localProject) {
        if (this.isMounted) {
          for (const field in localProject) {
            if (field !== 'address') {
              this.$store.set(`projects/${this.createPath}/edit@${field}`, localProject[field])
            }
          }

          for (const field in localProject.address) {
            this.$store.set(`projects/${this.createPath}/edit@address.${field}`, localProject.address[field])
          }
        }
      },
      deep: true
    }
  }
}
</script>

<style scoped>
  .col-half-offset{
    margin-left:0.80%
  }
  .row {
    display: flex;
    justify-content: space-around;
  }
  .card-wizard .tab-content {
    min-height: 0 !important;
  }
  .generate-project-name {
    margin-left: 5px;
    cursor: pointer;
  }
  label.el-checkbox.remember-address-field {
    margin-bottom: 0;
  }
  .box-visual {
    width: 100%;
    background-color: #f5f7fa;
    padding: 20px;
  }
  .project-details >>> .tiny-label-button {
    padding: 1px 5px;
    margin-top: -1px;
    float: right;
  }
</style>
